import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listReportViews, saveReportView, deleteReportView } from "@/lib/reporting/views.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { DataExplorer } from "@/components/reporting/data-explorer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/reports/builder")({ component: BuilderPage });

function BuilderPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const list = useServerFn(listReportViews);
  const save = useServerFn(saveReportView);
  const del = useServerFn(deleteReportView);

  const [activeView, setActiveView] = useState<{ id?: string; datasetKey: string; config: any } | null>(null);
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: views } = useQuery({
    queryKey: ["report-views", tenantId],
    enabled: !!tenantId,
    queryFn: () => list({ data: { tenantId: tenantId! } }),
  });

  const [currentQuery, setCurrentQuery] = useState<any>(null);

  async function onSave() {
    if (!tenantId || !name.trim() || !currentQuery) {
      toast.error("Run a query and enter a name first."); return;
    }
    setSaving(true);
    try {
      const { datasetKey, ...config } = currentQuery;
      await save({ data: {
        tenantId, name: name.trim(), datasetKey,
        config, visibility: "private", tags: [],
      }});
      toast.success("View saved");
      setOpen(false); setName("");
      qc.invalidateQueries({ queryKey: ["report-views", tenantId] });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to save");
    } finally { setSaving(false); }
  }

  async function onDelete(id: string) {
    await del({ data: { id } });
    toast.success("Deleted");
    qc.invalidateQueries({ queryKey: ["report-views", tenantId] });
    if (activeView?.id === id) setActiveView(null);
  }

  if (!tenantId) {
    return <><PageHeader title="Custom Report Builder" /><div className="p-6 text-sm text-muted-foreground">Select a tenant first.</div></>;
  }

  return (
    <>
      <PageHeader
        title="Custom Report Builder"
        description="Pick a dataset, choose columns, filter, group and aggregate. Save views for reuse."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" disabled={!currentQuery}><Save className="h-4 w-4 mr-1" /> Save view</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Save report view</DialogTitle></DialogHeader>
              <Input placeholder="View name" value={name} onChange={(e) => setName(e.target.value)} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={onSave} disabled={saving || !name.trim()}>
                  {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <Card><CardContent className="p-3 space-y-1">
          <div className="text-xs font-medium text-muted-foreground px-2 pb-2">Saved views</div>
          {(views?.views ?? []).length === 0 && <p className="text-xs text-muted-foreground px-2">No saved views yet.</p>}
          {(views?.views ?? []).map((v: any) => (
            <div key={v.id} className={`flex items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-accent cursor-pointer ${activeView?.id === v.id ? "bg-accent" : ""}`}
              onClick={() => setActiveView({ id: v.id, datasetKey: v.dataset_key, config: v.config })}>
              <div className="min-w-0 truncate">{v.name}</div>
              <Button size="icon" variant="ghost" className="h-6 w-6 opacity-60 hover:opacity-100"
                onClick={(e) => { e.stopPropagation(); onDelete(v.id); }}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </CardContent></Card>
        <DataExplorer
          key={activeView?.id ?? "new"}
          tenantId={tenantId}
          datasetKey={activeView?.datasetKey}
          initial={activeView ? { datasetKey: activeView.datasetKey, ...activeView.config } : undefined}
          onChange={setCurrentQuery}
        />
      </div>
    </>
  );
}